// src/comps/NewBroker.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';

const NewBroker = ({ item, show, onClose }) => {
  const empty = {
    id: '',
    fname: '',
    lname: '',
    dob: '',
    email: '',
    mobile: '',
    address: '',
    sub_centre: '',
    block: '',

    // Default lab commission (covers both pathology + radiology when overrides off)
    lab_commission_type: 'fixed',
    lab_commission: '',

    // ✅ NEW: Optional per-section overrides
    use_separate_lab: false,
    pathology_commission_type: 'fixed',
    pathology_commission: '',
    radiology_commission_type: 'fixed',
    radiology_commission: '',

    ipd_commission_type: 'fixed',
    ipd_commission: '',
    opd_commission_type: 'fixed',
    opd_commission: '',
    pharmacy_commission_type: 'fixed',
    pharmacy_commission: ''
  };

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      // detect whether record has separate path/radio values
      const hasPath =
        item.pathology_commission !== undefined &&
        item.pathology_commission !== null &&
        item.pathology_commission !== '';
      const hasRad =
        item.radiology_commission !== undefined &&
        item.radiology_commission !== null &&
        item.radiology_commission !== '';

      setForm({
        id: item.id || item.broker_id || '',
        fname: item.fname || '',
        lname: item.lname || '',
        dob: item.dob || '',
        email: item.email || '',
        mobile: item.mobile || '',
        address: item.address || item.address_line1 || '',
        sub_centre: item.sub_centre || '',
        block: item.block || '',

        lab_commission_type: item.lab_commission_type || 'fixed',
        lab_commission: item.lab_commission !== undefined ? item.lab_commission : '',

        use_separate_lab: !!(hasPath || hasRad),
        pathology_commission_type: item.pathology_commission_type || 'fixed',
        pathology_commission: hasPath ? item.pathology_commission : '',
        radiology_commission_type: item.radiology_commission_type || 'fixed',
        radiology_commission: hasRad ? item.radiology_commission : '',

        ipd_commission_type: item.ipd_commission_type || 'fixed',
        ipd_commission: item.ipd_commission !== undefined ? item.ipd_commission : '',
        opd_commission_type: item.opd_commission_type || 'fixed',
        opd_commission: item.opd_commission !== undefined ? item.opd_commission : '',
        pharmacy_commission_type: item.pharmacy_commission_type || 'fixed',
        pharmacy_commission: item.pharmacy_commission !== undefined ? item.pharmacy_commission : ''
      });
    } else {
      setForm(empty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, show]);

  const close = () => onClose(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      if (form.id) fd.append('id', form.id + '');
      fd.append('fname', form.fname);
      fd.append('lname', form.lname || '');
      fd.append('dob', form.dob || '');
      fd.append('email', form.email || '');
      fd.append('mobile', form.mobile || '');
      fd.append('address', form.address || '');
      fd.append('sub_centre', form.sub_centre || '');
      fd.append('block', form.block || '');

      // Lab (default)
      fd.append('lab_commission_type', form.lab_commission_type);
      fd.append('lab_commission', form.lab_commission || 0);

      // ✅ Pathology / Radiology overrides
      // Only send if user enabled them; otherwise send blank so backend can clear them
      if (form.use_separate_lab) {
        fd.append('pathology_commission_type', form.pathology_commission_type);
        fd.append('pathology_commission', form.pathology_commission || 0);
        fd.append('radiology_commission_type', form.radiology_commission_type);
        fd.append('radiology_commission', form.radiology_commission || 0);
      } else {
        // Clear overrides
        fd.append('pathology_commission_type', '');
        fd.append('pathology_commission', '');
        fd.append('radiology_commission_type', '');
        fd.append('radiology_commission', '');
      }

      fd.append('ipd_commission_type', form.ipd_commission_type);
      fd.append('ipd_commission', form.ipd_commission || 0);
      fd.append('opd_commission_type', form.opd_commission_type);
      fd.append('opd_commission', form.opd_commission || 0);
      fd.append('pharmacy_commission_type', form.pharmacy_commission_type);
      fd.append('pharmacy_commission', form.pharmacy_commission || 0);

      const res = await axios.post(AppConst.BASE_URL + AppConst.addBrokerUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data && res.data.status) {
        toast.success(res.data.success || 'Broker saved');
        onClose(true);
      } else {
        toast.error(res.data?.success || 'Save failed');
      }
    } catch (err) {
      console.error('save broker error', err);
      toast.error('Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block' }}>
      <div className="modal-dialog modal-lg">
        <form onSubmit={handleSubmit} className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{form.id ? 'Edit Broker' : 'Add Broker'}</h5>
            <button type="button" className="btn-close" onClick={close}></button>
          </div>
          <div className="modal-body">
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label">First name</label>
                <input className="form-control" value={form.fname}
                  onChange={(e) => setForm(f => ({ ...f, fname: e.target.value }))} required />
              </div>
              <div className="col-md-6">
                <label className="form-label">DOB</label>
                <input className="form-control" type="date" value={form.dob}
                  onChange={(e) => setForm(f => ({ ...f, dob: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <input className="form-control" value={form.mobile}
                  onChange={(e) => setForm(f => ({ ...f, mobile: e.target.value }))} />
              </div>
              <div className="col-md-12">
                <label className="form-label">Address</label>
                <textarea className="form-control" rows="2" value={form.address}
                  onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Sub Centre</label>
                <input
                  className="form-control"
                  value={form.sub_centre}
                  onChange={(e) => setForm(f => ({ ...f, sub_centre: e.target.value }))}
                  placeholder="Enter Sub Centre"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Block</label>
                <input
                  className="form-control"
                  value={form.block}
                  onChange={(e) => setForm(f => ({ ...f, block: e.target.value }))}
                  placeholder="Enter Block"
                />
              </div>

              <hr className="my-2" />
              <h6 className="text-primary mt-2">Commission Settings</h6>

              {/* Lab default */}
              <div className="col-md-6">
                <label className="form-label">Lab Commission Type</label>
                <select className="form-select" value={form.lab_commission_type}
                  onChange={(e) => setForm(f => ({ ...f, lab_commission_type: e.target.value }))}>
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Lab Commission (default for both)</label>
                <input className="form-control" value={form.lab_commission}
                  onChange={(e) => setForm(f => ({ ...f, lab_commission: e.target.value }))}
                  placeholder="Amount or percentage" />
              </div>

              {/* ✅ Optional: Separate Pathology / Radiology */}
              <div className="col-md-12 mt-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="useSeparateLab"
                    checked={form.use_separate_lab}
                    onChange={(e) =>
                      setForm(f => ({ ...f, use_separate_lab: e.target.checked }))
                    }
                  />
                  <label className="form-check-label" htmlFor="useSeparateLab">
                    Set <strong>different</strong> commissions for Pathology and Radiology
                  </label>
                </div>
                <small className="text-muted">
                  Leave unchecked to use the default Lab Commission above for both sections.
                </small>
              </div>

              {form.use_separate_lab && (
                <>
                  <div className="col-md-6">
                    <label className="form-label">Pathology Commission Type</label>
                    <select
                      className="form-select"
                      value={form.pathology_commission_type}
                      onChange={(e) =>
                        setForm(f => ({ ...f, pathology_commission_type: e.target.value }))
                      }
                    >
                      <option value="fixed">Fixed</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Pathology Commission</label>
                    <input
                      className="form-control"
                      value={form.pathology_commission}
                      onChange={(e) =>
                        setForm(f => ({ ...f, pathology_commission: e.target.value }))
                      }
                      placeholder="Amount or percentage"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Radiology Commission Type</label>
                    <select
                      className="form-select"
                      value={form.radiology_commission_type}
                      onChange={(e) =>
                        setForm(f => ({ ...f, radiology_commission_type: e.target.value }))
                      }
                    >
                      <option value="fixed">Fixed</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Radiology Commission</label>
                    <input
                      className="form-control"
                      value={form.radiology_commission}
                      onChange={(e) =>
                        setForm(f => ({ ...f, radiology_commission: e.target.value }))
                      }
                      placeholder="Amount or percentage"
                    />
                  </div>
                </>
              )}

              {/* IPD */}
              <div className="col-md-6">
                <label className="form-label">IPD Commission Type</label>
                <select className="form-select" value={form.ipd_commission_type}
                  onChange={(e) => setForm(f => ({ ...f, ipd_commission_type: e.target.value }))}>
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">IPD Commission</label>
                <input className="form-control" value={form.ipd_commission}
                  onChange={(e) => setForm(f => ({ ...f, ipd_commission: e.target.value }))}
                  placeholder="Amount or percentage" />
              </div>

              {/* OPD */}
              <div className="col-md-6">
                <label className="form-label">OPD Commission Type</label>
                <select className="form-select" value={form.opd_commission_type}
                  onChange={(e) => setForm(f => ({ ...f, opd_commission_type: e.target.value }))}>
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">OPD Commission</label>
                <input className="form-control" value={form.opd_commission}
                  onChange={(e) => setForm(f => ({ ...f, opd_commission: e.target.value }))}
                  placeholder="Amount or percentage" />
              </div>

              {/* Pharmacy */}
              <div className="col-md-6">
                <label className="form-label">Pharmacy Commission Type</label>
                <select className="form-select" value={form.pharmacy_commission_type}
                  onChange={(e) => setForm(f => ({ ...f, pharmacy_commission_type: e.target.value }))}>
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Pharmacy Commission</label>
                <input className="form-control" value={form.pharmacy_commission}
                  onChange={(e) => setForm(f => ({ ...f, pharmacy_commission: e.target.value }))}
                  placeholder="Amount or percentage" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (form.id ? 'Update Broker' : 'Add Broker')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewBroker;